
import {of as observableOf,  Observable ,  Subject } from 'rxjs';

import {map, shareReplay,  distinctUntilChanged } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import {
  Process, LocalizedProcess,
  ProcessActionNotificationType, ProcessParticipationApprovalRequirementType, ProcessRespondentsApproval, KnownProcessType
} from '../models/process-models';
import * as moment from 'moment';
import { ProcessAppraiseeApproval } from '../models/process-models';
import { ProcessFilter } from '../admin/processes-list/process-filter.model';
import { ToastrService } from 'ngx-toastr';
import { ProcessValidationHelper } from '../helpers/';
import { ReopenProcess } from '../models/process-models/reopen-process.model';
import { parseUtcDate, getShortDateString } from '../helpers/date-helper';
import { NamedEntity } from '../models/named-entity.model';

@Injectable()
export class ProcessService {

  employeeTypeChanged$: Observable<any> = new Observable();
  saveProcessClicked$: Observable<any> = new Observable();
  saveEmployeeSettings$: Observable<any> = new Observable();
  allAppraiseeApproval$: Observable<Array<ProcessAppraiseeApproval>>;
  private saveProcessSubject = new Subject<Process>();
  private saveEmployeeSettingsSubject = new Subject<Process>();
  private employeeTypeChangedSubject = new Subject();

  private readonly apiController = 'api/process';
  private readonly _360ApprovalLink = '360_approval';
  private readonly pmApprovalLink = 'pm_approval';
  private readonly hrApprovalLink = 'hr_approval';

    constructor(private httpService: HttpClient, private toastr: ToastrService) {
    this.saveProcessClicked$ = this.saveProcessSubject.asObservable();
    this.saveEmployeeSettings$ = this.saveEmployeeSettingsSubject.asObservable();
    this.employeeTypeChanged$ = this.employeeTypeChangedSubject.asObservable();
  }

  changeDeadline(date: Date, durationIds: Array<number>): Observable<any> {
    let dateString = getShortDateString(parseUtcDate(date));
    return this.httpService.put(`${this.apiController}/changeDeadline/${dateString}`, durationIds).pipe(distinctUntilChanged()) as Observable<any>;
  }

  getDistributionLists(filter: string = ""){
    return this.httpService.get(`${this.apiController}/distributionLists?filter=${filter}`) as Observable<Array<NamedEntity>>;
  }

  getAll(): Observable<Array<LocalizedProcess>> {
    return this.httpService.get(`${this.apiController}`) as Observable<Array<LocalizedProcess>>;
  }

  getByFilters(processFilter: ProcessFilter): Observable<Array<LocalizedProcess>> {
    return this.httpService
    .get(`${this.apiController}/filter${this.getProcessFilterQuery(processFilter)}`) as Observable<Array<LocalizedProcess>>;
  }

  getSelectedProcessInfo(httpParams: HttpParams): Observable<any> {
    return this.httpService.get(`${this.apiController}/selectedProcessInfo`, this.getRequestOptions(httpParams));
  }

  finalizeProcess(process: Process) {
    if (!ProcessValidationHelper.isProcessValid(process, this.toastr)) {
        return observableOf({ isProcessValid: false });
    }
    return this.httpService.put(`${this.apiController}/finalize/${process.id}`, null).pipe(distinctUntilChanged());
  }

  create(process: Process) {
    if (!ProcessValidationHelper.isProcessValid(process, this.toastr)) {
        return observableOf({});
    }
    return this.httpService.post(`${this.apiController}`, process).pipe(shareReplay()).pipe(distinctUntilChanged());
  }

  update(process: Process) {
    if (!ProcessValidationHelper.isProcessValid(process, this.toastr)) {
        return observableOf({});
    }
    return this.httpService.put(`${this.apiController}/${process.id}`, process).pipe(shareReplay()).pipe(distinctUntilChanged());
  }

  getOne(processId: number): Observable<Process> {
    return this.httpService.get(`${this.apiController}/${processId}`) as Observable<Process>;
  }

  getAllApproval(processApprovalRequirementType: ProcessParticipationApprovalRequirementType): Observable<Array<ProcessAppraiseeApproval>> {
    const actionLink = this.getActionLinkForApproval(processApprovalRequirementType);
    this.allAppraiseeApproval$ = this.httpService
      .get(`${this.apiController}/${actionLink}`).pipe(
      shareReplay()) as Observable<Array<ProcessAppraiseeApproval>>;
    return this.allAppraiseeApproval$;
  }

  getApprovalByPM(employeeId: string,
    processApprovalRequirementType: ProcessParticipationApprovalRequirementType): Observable<Array<ProcessAppraiseeApproval>> {
    return this.getAllApproval(processApprovalRequirementType).pipe(map(x => x.filter(p => p.appraisee.id === +employeeId)));
  }

  getApprovalSelfAssesment(processApprovalRequirementType: ProcessParticipationApprovalRequirementType):
    Observable<Array<ProcessAppraiseeApproval>> {
    if (!this.allAppraiseeApproval$) {
      this.getAllApproval(processApprovalRequirementType);
    }
    return this.allAppraiseeApproval$.pipe(map(x => x.filter(p => p.allowSelfAssesment)));
  }

  getApprovalByBU(businessUnitId: string,
    processApprovalRequirementType: ProcessParticipationApprovalRequirementType): Observable<Array<ProcessAppraiseeApproval>> {
       return this.getAllApproval(processApprovalRequirementType).pipe(map(x => x.filter(p => p.appraisee.businessUnitId === +businessUnitId)));
  }

  getProcessApprovalByAppraiseeDurationId(processApprovalRequirementType: ProcessParticipationApprovalRequirementType,
    processAppraiseeDurationId: number) {
    return this.getAllApproval(processApprovalRequirementType).pipe(
    map(x => x.find(p => p.processDurationAppraiseeId === processAppraiseeDurationId)));
  }

  getProcessRespondentsApproval(processAppraiseeDurationId: number): Observable<ProcessRespondentsApproval> {
    return this.httpService.get(`${this.apiController}/approval/edit-respondents/${processAppraiseeDurationId}`);
  }

  approveProcessAppraiseeRespondents(processDurationAppraiseeId: number, requirementType?: ProcessParticipationApprovalRequirementType) {
    const actionLink = this.getActionLinkForApproval(requirementType);
    return this.httpService.post(`${this.apiController}/${actionLink}/${processDurationAppraiseeId}`, processDurationAppraiseeId);
  }

  notifyAll(type: ProcessActionNotificationType): Observable<any> {
      return this.httpService.put(`${this.apiController}/notifyAll/${type}`, null)
          .pipe(distinctUntilChanged());
  }

  notifySelected(type: ProcessActionNotificationType, selectedProcesses: Array<LocalizedProcess>): Observable<any> {
    const durationIds = selectedProcesses.map(p => p.durationId);
      return this.httpService.put(`${this.apiController}/notifySelected/${type}`, durationIds)
          .pipe(distinctUntilChanged());
  }

  convertDateToString(date: Date) {
    return this.convertMomentDateToString(moment(date));
  }

  cancel(selectedProcesses: Array<LocalizedProcess>): Observable<any> {
    const ids = selectedProcesses.map(p => p.id);
      return this.httpService.put(`${this.apiController}/cancel`, ids)
          .pipe(distinctUntilChanged());
  }

  duplicate(selectedProcesses: Array<LocalizedProcess>): Observable<any> {
    const ids = selectedProcesses.map(p => p.id);
    return this.httpService.post(`${this.apiController}/duplicate`, ids);
  }

  sendHrSummaryNotification(ids: Array<number>): Observable<any> {
    return this.httpService.post(`${this.apiController}/summaryHr/`, ids);
  }

  reopen(reopenModel: ReopenProcess): Observable<any> {
    return this.httpService.put(`${this.apiController}/reopen`, reopenModel);
  }

  convertMomentDateToString(date: moment.Moment) {
    return date.format('YYYY-MM-DD');
  }

  parseDate(date: string) {
    return moment(date).toDate();
  }

  toggleEnumSetting<T>(array: Array<T>, enumSetting: T): boolean {
    const index = array.indexOf(enumSetting);
    const isInArray = index !== -1;
    if (isInArray) {
      array.splice(index, 1);
    } else { array.push(enumSetting); }
    return !isInArray;
  }

  onEmployeeTypeChanged() {
    this.employeeTypeChangedSubject.next();
  }

  saveButtonClicked() {
    this.saveProcessSubject.next();
  }

  saveEmployeeSettings() {
    this.saveEmployeeSettingsSubject.next();
  }

  getIs360SetOnly(process: Process) {
    return process.processKnownTypes 
      && process.processKnownTypes.length === 1
      && process.processKnownTypes.indexOf(KnownProcessType._360) !== -1;
  }

  private getRequestOptions(httpParams?: HttpParams): Object {
    const options = {
      headers: new HttpHeaders(
        {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
      responseType: 'blob',
      params: httpParams
    };
    return options;
  }

  private getProcessFilterQuery(processFilter: ProcessFilter){
    return `?filter.businessUnitId=${processFilter.businessUnitId}
            &filter.employeeId=${processFilter.employeeId}
            &filter.gradeId=${processFilter.gradeId}
            &filter.respondentId=${processFilter.respondentId}`;
  }

  private getActionLinkForApproval(processApprovalRequirementType: ProcessParticipationApprovalRequirementType): string {
    let actionLink = this._360ApprovalLink;
    if (processApprovalRequirementType === ProcessParticipationApprovalRequirementType.ByPerformanceManager) {
      actionLink = this.pmApprovalLink;
    }
    if (processApprovalRequirementType === ProcessParticipationApprovalRequirementType.ByDepartmentAdministrator) {
      actionLink = this.hrApprovalLink;
    }
    return actionLink;
  }
}
